<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" scrollable
                   :title="computedTitle" @show="onShow"
                   :disable-submit-btn="!model.invoices || model.invoices.length === 0 || disableForm"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading || submitting" modal-class="mobile-full-screen">

      <div class="row">
        <div class="col-md-3">
          <input-widget :model="model" attribute="date" type="datepicker" :disabled="disableForm"/>
        </div>
        <div class="col-md-3">
          <input-widget :model="model" attribute="method" value-field="value" display-field="${item.key}"
                        @select2Select="onMethodSelect"
                        type="select2" :items="paymentMethods" :disabled="disableForm"/>
        </div>
        <div class="col-md-3" v-if="showFeePercent">
          <input-widget :model="model" attribute="fee_percent" type="number" rules="min:0"/>
        </div>
        <div class="col-md-3" v-if="paymentBankAccounts && paymentBankAccounts.length > 0">
          <input-widget :model="model" attribute="bank_account" value-field="value"
                        display-field="${item.key}" type="select2" :items="paymentBankAccounts"
                        :disabled="disableForm"/>
        </div>
        <div class="col-md-3">
          <input-widget :model="model" attribute="status" value-field="value" display-field="${item.key}" type="select2"
                        :items="batchPaymentStatuses" :disabled="disableForm"/>
        </div>

        <div class="col-md-3">
          <currency-select-input-widget :model="model" attribute="currency_uuid"
                                        @select="onCurrencySelect" :disabled="!!model.uuid"/>
        </div>

        <div class="col-md-3">
          <input-widget :model="model" attribute="exchange_rate" :rules="exchangeRateRules" type="number"
                        @input="onExchangeRateChange"/>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <input-widget :model="model" attribute="description" type="textarea" :disabled="disableForm"/>
        </div>
      </div>

      <div class="row mt-3 mb-2">
        <div class="col-md-12 d-flex justify-content-between">
          <h5 class="mb-0">{{ $t('Invoices') }}</h5>
          <b-button :disabled="model.invoices.length > 199 || disableForm"
                    size="sm" variant="primary" @click="onAddInvoiceClick()">
            <font-awesome-icon icon="plus" :disabled="disableForm"/>
            {{ $t('Add Invoice') }}
          </b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <table class="info-table table table-sm lots-table">
            <tr>
              <th style="width: 20%">{{ $t('Invoice') }} <span class="text-danger">*</span></th>
              <th style="width: 15%">{{ $t('Amount') }} <span class="text-danger">*</span></th>
              <th style="width: 15%" v-if="currenciesDiffer">{{ $t('Converted Amount') }} <span
                class="text-danger">*</span></th>
              <th style="width: 15%">{{ $t('Reference Number') }}</th>
              <th style="width: 15%">{{ $t('Customer') }}</th>
              <th style="width: 15%">{{ $t('Info') }}</th>
              <th v-if="showFeePercent" style="width: 10%">{{ $t('Surcharge') }}</th>
              <th style="width: 10%">{{ $t('Reconciled') }}</th>
              <th style="width: 5%">{{ $t('Actions') }}</th>
            </tr>
            <tr v-if="model.invoices.length === 0">
              <td colspan="7">
                <no-data-available height="4"/>
              </td>
            </tr>
            <template v-else>
              <template v-for="(invoiceItem, idx) in model.invoices">
                <tr :key="invoiceItem.uniqueKey">
                  <td>
                    <invoice-select-input-widget :model="invoiceItem" attribute="invoice_uuid" :label="false"
                                                 :options="{'placeholder': $t('Invoice')}"
                                                 @select="onInvoiceSelect($event, invoiceItem, idx)"
                                                 @unselect="onInvoiceUnselect(invoiceItem)"
                                                 :disabled="disableForm || !!invoiceItem.uuid"
                                                 :extra-filters="{expand: 'customer.deposits.currency,customer.deposit_reservations.currency,customer.deposit_reservations.auction,currency'}"/>
                  </td>
                  <td>
                    <currency-input-widget :model="invoiceItem" attribute="amount" :label="false"
                                           :placeholder="$t('Amount')" :disabled="disableForm"
                                           :currency="modelCurrencySymbol" @input="onAmountChange(invoiceItem)"/>
                  </td>
                  <td v-if="currenciesDiffer">
                    <currency-input-widget :model="invoiceItem" attribute="converted_amount" :label="false"
                                           :disabled="disableForm"
                                           :currency="invoiceCurrencySymbol"
                                           @input="onConvertedAmountChange(invoiceItem)"/>
                  </td>
                  <td>
                    <input-widget :model="invoiceItem" attribute="reference_number" type="text" rules="max:11"
                                  :label="false" :disabled="disableForm"/>
                  </td>
                  <td>
                    <div v-if="invoiceItem.invoice && invoiceItem.invoice.customer" style="font-size: 12px">
                      {{ invoiceItem.invoice.customer.customer_no }} <br>
                      {{ invoiceItem.invoice.customer.display_name }} <br>
                      {{ invoiceItem.invoice.customer.email }}
                    </div>
                  </td>
                  <td>
                    <div v-if="invoiceItem.invoice" style="font-size: 12px">
                      <b>{{ $t('Total') }}:</b> {{
                        invoiceItem.invoice.total_amount|asCurrency(modelCurrencySymbol)
                      }}
                      <br>
                      <b>{{ $t('Paid') }}:</b>
                      {{ invoiceItem.invoice.paid_amount|asCurrency(modelCurrencySymbol) }}
                      <div v-if="model.method === 'deposit' && invoiceItem.invoice.customer.deposit">
                        <b>{{ $t('Available balance') }}:</b>
                        {{
                          invoiceItem.invoice.customer.deposit.available_balance | asCurrency(modelCurrencySymbol)
                        }}
                        <br>

                        <div v-for="depositReservation in invoiceItem.invoice.customer.depositReservations"
                             :key="`dept-rest-${depositReservation.uuid}-${invoiceItem.invoice.uuid}`"
                             class="mb-1">
                          <div class="mr-2 d-inline-block font-weight-bold">
                            {{ $t('Reserved for Sale ') }}
                            <a href="javascript:void(0)" v-if="depositReservation.auction && !enableContinuousAuction"
                               @click="onSaleViewClick(depositReservation)">
                              {{ depositReservation.auction.sale_no }}
                            </a>
                            <div v-else-if="depositReservation.auction && enableContinuousAuction"
                                 class="d-inline-block">
                              {{ depositReservation.auction.sale_no }}
                            </div>
                            <live-sale-ref v-if="depositReservation.auction.live_reference"
                                           :href="depositReservation.auction.live_url"
                                           :label="depositReservation.auction.live_reference"/>
                            :
                          </div>
                          {{ depositReservation.amount | asCurrency(modelCurrencySymbol) }}
                        </div>

                      </div>
                    </div>
                  </td>
                  <td class="text-center" v-if="showFeePercent && invoiceItem.invoice && invoiceItem.amount">
                    <span>{{
                        getPaymentFeeAmount(invoiceItem.amount) | asCurrency(invoiceItem.invoice.currency.symbol)
                      }}</span>
                  </td>
                  <td class="text-center">
                    <input-widget :model="invoiceItem" attribute="reconciled" type="checkbox" :label="false"
                                  :disabled="disableForm"/>
                  </td>
                  <td class="text-center">
                    <b-button variant="danger" size="sm" @click="onInvoiceItemRemoveClick(invoiceItem)"
                              :disabled="disableForm">
                      <font-awesome-icon icon="times" class="icon"/>
                    </b-button>
                  </td>
                </tr>
                <tr :key="`warning-row-${invoiceItem.uniqueKey}`">
                  <td colspan="7" class="border-top-0 text-danger" v-if="invoiceItem.invoice">
                    <div
                      v-if="!model.uuid && invoiceItem.amount && invoiceItem.amount > invoiceItem.invoice.outstanding_amount">
                      {{ $t('The payment amount is higher than the outstanding amount of the invoice.') }}
                    </div>
                    <div v-if="invoiceItem.invoice.status === 'paid' ">
                      {{ $t('Following invoice is fully paid.') }}
                    </div>
                  </td>
                </tr>
              </template>


            </template>
          </table>
        </div>
      </div>

      <div class="row mb-2">
        <div class="col-md-3 offset-9">
          <table class="w-100 text-right">
            <tr>
              <td class="w-75"><b>{{ $t('Total Payment Amount') }}:</b></td>
              <td class="w-25">{{ totalAmount|asCurrency(modelCurrencySymbol) }}</td>
            </tr>
            <tr>
              <td class="w-75"><b>{{ $t('Number of Entries') }}:</b></td>
              <td class="w-25">{{ model.invoices.length }}</td>
            </tr>
          </table>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import InvoiceSelectInputWidget from "@/core/widgets/InvoiceSelectInputWidget";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import InvoiceBatchPaymentModel from "@/modules/invoices/invoice-batch-payment/InvoiceBatchPayment.model";
  import InvoiceBatchPaymentItemModel from "@/modules/invoices/invoice-batch-payment/InvoiceBatchPaymentItem.model";
  import InvoicesService from "@/modules/invoices/InvoicesService";
  import {SOURCE_INVOICE_PAYMENTS_TABLE, SOURCE_INVOICES_TABLE} from "@/modules/constants";
  import cloneDeep from "lodash/cloneDeep";
  import LiveSaleRef from "@/core/components/common/LiveSaleRef";
  import CurrencySelectInputWidget from "@/core/widgets/CurrencySelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: globalMapState} = createNamespacedHelpers('global');
  const {mapActions: invoicePaymentsActions} = createNamespacedHelpers('invoicePayments');

  export default {
    name: "InvoiceBatchPaymentModal",
    components: {
      CurrencySelectInputWidget,
      LiveSaleRef,
      InputWidget,
      CurrencyInputWidget,
      InvoiceSelectInputWidget,
      NoDataAvailable,
      ArtisioModal
    },
    data() {
      return {
        showConfirmation: true,
        model: new InvoiceBatchPaymentModel(),
        oldStatus: null,
        showFeePercent: false,
        submitting: false
      }
    },
    computed: {
      ...mapState({
        modal: state => state.invoiceBatchPaymentModal,
        loading: state => state.invoiceBatchPaymentModal.loading,
        editObject: state => state.invoiceBatchPaymentModal.object,
      }),
      ...globalMapState({
        currencies: state => state.currencies,
        paymentBankAccounts: state => state.paymentBankAccounts,
        batchPaymentStatuses: state => state.batchPaymentStatuses,
        paymentMethods: state => state.paymentMethods,
        enableContinuousAuction: state => state.enableContinuousAuction,
      }),
      computedTitle() {
        if (!this.model.uuid || !this.editObject) {
          return this.$i18n.t('Generate Batch Payments')
        }
        return this.$i18n.t('Update Batch Payments - {batch_id}', {batch_id: this.editObject.payment_no})
      },
      totalAmount() {
        let tmpAmount = 0
        this.model.invoices.forEach(i => tmpAmount += parseFloat(i.amount ? i.amount : 0))
        return tmpAmount
      },
      disableForm() {
        return this.model.uuid && this.oldStatus === 'approved'
      },
      currenciesDiffer() {
        if (this.model.currency_uuid && this.model.invoices.length > 0) {
          return this.model.currency_uuid !== this.model.invoices[0].invoice.currency.uuid
        }
        return this.model.currency_uuid
      },
      exchangeRateRules() {
        return this.currenciesDiffer ? 'required' : ''
      },
      modelCurrencySymbol() {
        let tmpCurrency = this.currencies.find(c => c.uuid === this.model.currency_uuid)
        return tmpCurrency ? tmpCurrency.symbol : null
      },
      invoiceCurrencySymbol() {
        if (this.model.invoices.length > 0) {
          return this.model.invoices[0].invoice.currency.symbol
        }
        return null
      }
    },
    watch: {
      editObject() {
        this.showFeePercent = false;
        if (this.editObject && this.editObject.uuid) {
          let tmpObj = cloneDeep(this.editObject)
          let payments = tmpObj.payments
          delete tmpObj.payments

          this.model = new InvoiceBatchPaymentModel(tmpObj)
          let objPayments = []
          for (let payment of payments) {
            let invoicePayment = payment.invoice_payments[0]
            let invoice = cloneDeep(invoicePayment.invoice)
            this.calculateDepositInfo(invoice)
            objPayments.push(new InvoiceBatchPaymentItemModel({
              uuid: payment.uuid,
              invoice_uuid: invoicePayment.invoice.uuid,
              invoice: invoice,
              amount: payment.amount,
              converted_amount: invoicePayment.converted_amount,
              reference_number: payment.reference_number,
              reconciled: payment.reconciled,
            }))
          }
          this.model.invoices = objPayments
          this.oldStatus = this.model.status
        }
      }
    },
    methods: {
      ...mapActions(['hideInvoiceBatchPaymentModal', 'getTableData', 'toggleInvoiceBatchPaymentModalLoading']),
      ...invoicePaymentsActions({invoicePaymentsGetTableData: 'getTableData'}),

      async onShow() {
        this.showConfirmation = true;
      },
      onHidden() {
        this.model = new InvoiceBatchPaymentModel();
        this.hideInvoiceBatchPaymentModal();
        this.showFeePercent = false;
        this.submitting = false;
      },
      async onSubmit() {
        if (this.submitting) {
          return;
        }
        this.submitting = true;
        try {
          await this.submitBatchPayment();
        } finally {
          this.submitting = false;
        }
      },
      async submitBatchPayment() {
        let currency = this.model.invoices[0].invoice.currency

        for (let item of this.model.invoices) {
          if (currency.uuid !== item.invoice.currency.uuid) {
            this.$errorDialog('You cannot create batch payment for invoices with different currencies.')
            return false
          }
        }

        this.showConfirmation = false

        this.toggleInvoiceBatchPaymentModalLoading(true)

        let requestData = {
          uuid: this.model.uuid,
          date: this.model.date,
          method: this.model.method,
          description: this.model.description,
          bank_account: this.model.bank_account,
          fee_percent: this.model.fee_percent,
          status: this.model.status,
          currency_uuid: this.model.currency_uuid,
          exchange_rate: this.model.exchange_rate,
          payments: this.model.invoices.map(i => {
            return {
              uuid: i.uuid,
              invoice_uuid: i.invoice_uuid,
              amount: i.amount,
              reference_number: i.reference_number,
              reconciled: i.reconciled,
            }
          })
        }

        let response;
        if (!requestData.uuid) {
          response = await InvoicesService.createBatchPayment(requestData)
        } else {
          response = await InvoicesService.updateBatchPayment(requestData.uuid, requestData)
        }

        if (response.success) {
          if (!requestData.uuid) {
            this.$toast('success', this.$t('Batch payment was successfully created'));
          } else {
            this.$toast('success', this.$t('Batch payment was successfully updated'));
          }


          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            await this.getTableData();
          } else if (this.modal.source === SOURCE_INVOICE_PAYMENTS_TABLE) {
            await this.invoicePaymentsGetTableData()
          }
          this.toggleInvoiceBatchPaymentModalLoading(false)
          this.hideInvoiceBatchPaymentModal();

        } else {

          this.toggleInvoiceBatchPaymentModalLoading(false)

          this.showConfirmation = true
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Batch Payment Error'));
          }
        }
      },
      onAddInvoiceClick() {
        this.model.invoices.push(new InvoiceBatchPaymentItemModel())
      },
      onInvoiceItemRemoveClick(item) {
        let index = this.model.invoices.findIndex(i => i.uniqueKey === item.uniqueKey)
        if (index > -1) {
          this.model.invoices.splice(index, 1)
        }
      },
      onInvoiceSelect(data, invoiceItem, idx) {
        invoiceItem.invoice = data
        if (data.outstanding_amount >= 0) {
          invoiceItem.amount = data.outstanding_amount
          invoiceItem.initial_amount = data.outstanding_amount
        }

        let showError = false
        let invoiceCurrencyUuid = this.model.invoices.length > 0 ? this.model.invoices[0].invoice.currency.uuid : null

        if (!this.model.currency_uuid) {
          this.model.currency_uuid = invoiceCurrencyUuid
        }

        for (let item of this.model.invoices) {
          if (item.invoice && invoiceCurrencyUuid !== item.invoice.currency.uuid) {
            showError = true
          }
        }

        if (!showError) {
          this.calculateDepositInfo(data)
        } else {
          this.model.invoices[idx] = new InvoiceBatchPaymentItemModel()
          this.$errorDialog(this.$i18n.t('You can not create batch payment for different currencies'))
        }
      },
      calculateDepositInfo(data) {
        if (data.customer) {
          if (data.customer.deposits && data.customer.deposits.length > 0) {
            data.customer.deposit = data.customer.deposits.find(d => d.currency_uuid === data.currency_uuid)
          }

          const depositReservations = []
          if (data.auction_uuid && data.customer.deposit_reservations && data.customer.deposit_reservations.length > 0) {
            for (let depositReservation of data.customer.deposit_reservations) {
              if (depositReservation.currency_uuid === data.currency_uuid && depositReservation.auction.uuid === data.auction_uuid) {
                depositReservations.push(depositReservation);
              }
            }
          }
          data.customer.depositReservations = depositReservations
        }
      },
      onSaleViewClick(depositReservation) {
        this.showConfirmation = false
        this.hideInvoiceBatchPaymentModal()
        this.$router.push(
          {name: 'sales.lots', params: {id: depositReservation.auction.uuid}}
        )
      },
      onInvoiceUnselect(invoiceItem) {
        invoiceItem.invoice = null
        invoiceItem.invoice_uuid = null
        invoiceItem.amount = null
      },
      onCurrencySelect() {
        this.model.exchange_rate = null
      },
      onExchangeRateChange() {
        this.$nextTick(() => {
          this.calculateAmountAll()
        })
      },
      calculateAmountAll() {
        for (let item of this.model.invoices) {
          this.calculateAmount(item)
        }
      },
      calculateAmount(item, amountSource = null) {
        if (amountSource === null) {
          amountSource = item.initial_amount
        }

        if (this.model.exchange_rate) {
          item.amount = (amountSource / this.model.exchange_rate).toFixed(2)
        }
        item.converted_amount = amountSource
      },
      onAmountChange(invoiceItem) {
        if (!this.currenciesDiffer || !this.model.exchange_rate) {
          return true
        }
        invoiceItem.converted_amount = (invoiceItem.amount * this.model.exchange_rate).toFixed(2)
      },
      onConvertedAmountChange(invoiceItem) {
        this.calculateAmount(invoiceItem, invoiceItem.converted_amount)
      },
      onMethodSelect(data) {
        const paymentMethod = this.paymentMethods.find(p => p.value === data.value);
        if (paymentMethod.hasOwnProperty('fee_percent')) {
          this.showFeePercent = true;
          this.model.fee_percent = paymentMethod.fee_percent;
        } else {
          this.model.fee_percent = null;
        }
      },
      getPaymentFeeAmount(amount) {
        if (!amount || !this.model.fee_percent) {
          return 0;
        }
        return amount - Math.round((amount / (1 + this.model.fee_percent / 100) * 100)) / 100;
      },
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../scss/variables";

  .lots-table {
    /deep/ .form-group {
      margin-bottom: 0;
    }
  }

</style>
